import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Grid3X3, 
  List, 
  Eye, 
  Edit, 
  Edit3,
  Star, 
  Lock, 
  Unlock, 
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  Palette,
  User,
  Building2,
  BookOpen,
  Image,
  Crown,
  X,
  Plus,
  Volume2,
  Tag,
  FileText,
  MapPin,
  Printer,
  ImagePlus,
  Mic,
  CheckCircle2,
  AlertCircle,
  BarChart3
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';

import '../styles/ArtworkAdmin.css';

// Helper to add f_auto transformation for format conversion (HEIC -> WebP/JPEG)
// without cropping or resizing - preserves original aspect ratio
const getDisplayImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  if (imageUrl.includes('cloudinary.com') && imageUrl.includes('/upload/')) {
    return imageUrl.replace('/upload/', '/upload/f_auto,q_auto/');
  }
  return imageUrl;
};

const ArtworkAdmin = ({ artistFilter = null }) => {
  const navigate = useNavigate();
  
  // State management
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [priceFilter, setPriceFilter] = useState({ min: '', max: '' });
  const [audioFilter, setAudioFilter] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [totalCount, setTotalCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [isPageLoading, setIsPageLoading] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  // Collection selector state
  const [collectionSelectorOpen, setCollectionSelectorOpen] = useState(false);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [lastSelectedCollection, setLastSelectedCollection] = useState(null);

  // Collections view modal state
  const [collectionsViewOpen, setCollectionsViewOpen] = useState(false);
  const [currentCollections, setCurrentCollections] = useState([]);

  // Enrichment aggregate stats
  const [enrichmentStats, setEnrichmentStats] = useState(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);

  // Collection filter state
  const [collectionFilterId, setCollectionFilterId] = useState('');
  const [collectionFilterSearch, setCollectionFilterSearch] = useState('');
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false);
  const [filterCollections, setFilterCollections] = useState([]);
  const collectionFilterRef = useRef(null);

  // Navigate to artwork detail
  const handleArtworkClick = (vcId) => {
    navigate(`/admin/artworks/${vcId}`);
  };

  // Calculate range for Supabase query
  const from = (currentPage - 1) * pageSize;
  const to = from + pageSize - 1;

  // Fetch artworks data
  const fetchArtworks = async () => {
    if (currentPage === 1) {
      setLoading(true);
    } else {
      setIsPageLoading(true);
    }
    setLoadingProgress({ current: 0, total: 0 });
    
    try {
      console.log(`Executing query with range: ${from} to ${to}`);

      // If collection filter is active, get the vc_ids in that collection first
      let collectionVcIds = null;
      if (collectionFilterId) {
        const { data: colMappings } = await supabase
          .from('collection_vc_mapping')
          .select('vc_id')
          .eq('collection_id', parseInt(collectionFilterId, 10));
        collectionVcIds = (colMappings || []).map(m => m.vc_id);
        if (collectionVcIds.length === 0) {
          // No artworks in this collection — show empty state
          setTotalCount(0);
          setArtworks([]);
          setLoading(false);
          setIsPageLoading(false);
          return;
        }
      }
      
      // First, get the total count (only on first page or when filters change)
      if (currentPage === 1) {
        let countQuery = supabase
          .from('vcs')
          .select('*', { count: 'exact', head: true })
          .eq('access_control', 'Public')
          .ilike('cover_image', '%res.cloudinary.com/dh9jfogh1%');
        
        if (audioFilter) {
          countQuery = countQuery
            .select('*, media_elements!inner(id, type, status)', { count: 'exact', head: true })
            .eq('media_elements.type', 'Audio')
            .eq('media_elements.status', 'Public');
        }
        
        // Apply artist filter to count query if provided
        if (artistFilter) {
          countQuery = countQuery.eq('users.uuid', artistFilter);
        }

        // Apply collection filter to count query
        if (collectionVcIds) {
          countQuery = countQuery.in('vc_id', collectionVcIds);
        }
        
        const { count: totalCountResult } = await countQuery;
        setTotalCount(totalCountResult || 0);
      }
      
      // Then fetch the paginated artworks
      let query;
      
      if (audioFilter) {
        // When filtering for audio, we need to join with media_elements
        query = supabase
          .from('vcs')
          .select(`
            vc_id, title, description, cover_image, price, width, height, depth, status, location, artwork_completed_date, access_control, created_at, updated_at, uuid,
            users!inner(uuid, nickname, first_name, last_name, handle),
            media_elements!inner(id, type, source, status)
          `)
          .eq('access_control', 'Public')
          .ilike('cover_image', '%res.cloudinary.com/dh9jfogh1%')
          .eq('media_elements.type', 'Audio')
          .eq('media_elements.status', 'Public')
          .range(from, to);
      } else {
        query = supabase
          .from('vcs')
          .select(`
            vc_id, title, description, cover_image, price, width, height, depth, status, location, artwork_completed_date, access_control, created_at, updated_at, uuid,
            users!inner(uuid, nickname, first_name, last_name, handle)
          `)
          .eq('access_control', 'Public')
          .ilike('cover_image', '%res.cloudinary.com/dh9jfogh1%')
          .range(from, to);
      }

      // Apply artist filter if provided
      if (artistFilter) {
        query = query.eq('users.uuid', artistFilter);
      }

      // Apply collection filter
      if (collectionVcIds) {
        query = query.in('vc_id', collectionVcIds);
      }

      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,users.nickname.ilike.%${searchQuery}%,users.handle.ilike.%${searchQuery}%`);
      }

      if (priceFilter.min !== '' || priceFilter.max !== '') {
        if (priceFilter.min !== '') {
          query = query.gte('price', parseFloat(priceFilter.min));
        }
        if (priceFilter.max !== '') {
          query = query.lte('price', parseFloat(priceFilter.max));
        }
      }

      // Apply sorting
      console.log('Applying sorting:', { sortBy, sortOrder });
      if (sortBy === 'price') {
        query = query.order('price', { ascending: sortOrder === 'asc' });
      } else if (sortBy === 'title') {
        query = query.order('title', { ascending: sortOrder === 'asc' });
      } else if (sortBy === 'artist') {
        query = query.order('users.nickname', { ascending: sortOrder === 'asc' });
      } else {
        // Default: created_at
        query = query.order('created_at', { ascending: sortOrder === 'asc' });
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching artworks:', error);
        return;
      }

      console.log('Query result:', { data, error, count: totalCount });

      // --- Batch enrichment lookups (replaces N+1 per-artwork queries) ---
      const vcIds = data.map(a => a.vc_id);

      // 1. Collection counts
      const { data: collectionMappings } = await supabase
        .from('collection_vc_mapping')
        .select('vc_id')
        .in('vc_id', vcIds);
      const collectionCounts = {};
      (collectionMappings || []).forEach(m => {
        collectionCounts[m.vc_id] = (collectionCounts[m.vc_id] || 0) + 1;
      });

      // 2. Tags (all types) per artwork
      const { data: tagMappings } = await supabase
        .from('attribute_vc_mapping')
        .select('vc_id, artwork_attributes!inner(title, type)')
        .in('vc_id', vcIds);
      const tagData = {};
      (tagMappings || []).forEach(t => {
        if (!tagData[t.vc_id]) tagData[t.vc_id] = { count: 0, medium: null };
        tagData[t.vc_id].count++;
        if (t.artwork_attributes.type === 'medium' && !tagData[t.vc_id].medium) {
          tagData[t.vc_id].medium = t.artwork_attributes.title;
        }
      });

      // 3. Products (prints) per artwork
      const { data: printRows } = await supabase
        .from('products')
        .select('vc_id')
        .in('vc_id', vcIds)
        .eq('type', 'print');
      const printSet = new Set((printRows || []).map(p => p.vc_id));

      // 4. Media elements per artwork (stories = Writing, images = Image, audio = Audio)
      const { data: mediaRows } = await supabase
        .from('media_elements')
        .select('vc_id, type')
        .in('vc_id', vcIds)
        .in('type', ['Image', 'Audio', 'Writing']);
      const mediaData = {};
      (mediaRows || []).forEach(m => {
        if (!mediaData[m.vc_id]) mediaData[m.vc_id] = { images: 0, audio: 0, writing: 0 };
        if (m.type === 'Image') mediaData[m.vc_id].images++;
        else if (m.type === 'Audio') mediaData[m.vc_id].audio++;
        else if (m.type === 'Writing') mediaData[m.vc_id].writing++;
      });

      // Build enriched artwork objects
      const artworksWithDetails = data.map(artwork => {
        const artist_name = artwork.users?.nickname ||
          `${artwork.users?.first_name || ''} ${artwork.users?.last_name || ''}`.trim() ||
          artwork.users?.handle || 'Unknown Artist';

        const tags = tagData[artwork.vc_id] || { count: 0, medium: null };
        const media = mediaData[artwork.vc_id] || { images: 0, audio: 0, writing: 0 };

        // Calculate enrichment score (out of 11)
        const enrichment = {
          hasTitle: !!(artwork.title && artwork.title.trim()),
          hasStatus: !!(artwork.status && artwork.status.trim()),
          hasPrice: !!(artwork.price && artwork.price > 0),
          hasLocation: !!artwork.location,
          hasPrints: printSet.has(artwork.vc_id),
          hasCompletionDate: !!artwork.artwork_completed_date,
          hasDimensions: !!(artwork.width && artwork.height),
          hasTags: tags.count > 0,
          hasStory: media.writing > 0,
          hasAdditionalImages: media.images > 0,
          hasAudio: media.audio > 0,
        };
        const enrichmentScore = Object.values(enrichment).filter(Boolean).length;

        return {
          ...artwork,
          collection_count: collectionCounts[artwork.vc_id] || 0,
          medium: tags.medium || 'Unknown',
          artist_name,
          tag_count: tags.count,
          has_prints: printSet.has(artwork.vc_id),
          additional_images: media.images,
          audio_count: media.audio,
          story_count: media.writing,
          enrichment,
          enrichment_score: enrichmentScore,
        };
      });

      console.log('Artworks with enrichment:', artworksWithDetails);
      setArtworks(artworksWithDetails);
    } catch (error) {
      console.error('Error in fetchArtworks:', error);
    } finally {
      setLoading(false);
      setIsPageLoading(false);
      setLoadingProgress({ current: 0, total: 0 });
    }
  };

  // Fetch aggregate enrichment stats across all public artworks (or filtered set)
  const fetchEnrichmentStats = useCallback(async (opts = {}) => {
    const { collectionId, search } = opts;
    setEnrichmentLoading(true);
    try {
      // Helper: paginate through all rows (Supabase caps at 1000 per request)
      const fetchAllRows = async (queryBuilder) => {
        const PAGE = 1000;
        let allRows = [];
        let from = 0;
        while (true) {
          const { data, error } = await queryBuilder().range(from, from + PAGE - 1);
          if (error) throw error;
          allRows = allRows.concat(data || []);
          if (!data || data.length < PAGE) break;
          from += PAGE;
        }
        return allRows;
      };

      // Helper: chunked .in() queries (PostgREST has URL length limits)
      const fetchInChunks = async (table, selectCols, filterCol, ids, extraFilters) => {
        const CHUNK = 500;
        let allRows = [];
        for (let i = 0; i < ids.length; i += CHUNK) {
          const chunk = ids.slice(i, i + CHUNK);
          let q = supabase.from(table).select(selectCols).in(filterCol, chunk);
          if (extraFilters) q = extraFilters(q);
          let from = 0;
          while (true) {
            const { data, error } = await q.range(from, from + 999);
            if (error) throw error;
            allRows = allRows.concat(data || []);
            if (!data || data.length < 1000) break;
            from += 1000;
            q = supabase.from(table).select(selectCols).in(filterCol, chunk);
            if (extraFilters) q = extraFilters(q);
          }
        }
        return allRows;
      };

      // If collection filter active, get vc_ids in that collection first
      let collectionVcIds = null;
      if (collectionId) {
        const { data: colMappings } = await supabase
          .from('collection_vc_mapping')
          .select('vc_id')
          .eq('collection_id', parseInt(collectionId, 10));
        collectionVcIds = (colMappings || []).map(m => m.vc_id);
        if (collectionVcIds.length === 0) {
          setEnrichmentStats({ total: 0 });
          return;
        }
      }

      // Get ALL public artworks (paginated) — include `more` for story calculation
      const allArtworks = await fetchAllRows(() => {
        let q = supabase
          .from('vcs')
          .select('vc_id, title, price, status, location, artwork_completed_date, width, height, description, more, created_at, users!inner(nickname, handle)')
          .eq('access_control', 'Public')
          .ilike('cover_image', '%res.cloudinary.com/dh9jfogh1%');
        if (artistFilter) q = q.eq('uuid', artistFilter);
        if (collectionVcIds) q = q.in('vc_id', collectionVcIds);
        if (search) q = q.or(`title.ilike.%${search}%,users.nickname.ilike.%${search}%,users.handle.ilike.%${search}%`);
        return q;
      });

      const total = allArtworks.length;
      if (total === 0) {
        setEnrichmentStats({ total: 0 });
        return;
      }

      const vcIds = allArtworks.map(a => a.vc_id);

      // Batch: tags (chunked)
      const tagRows = await fetchInChunks('attribute_vc_mapping', 'vc_id', 'vc_id', vcIds);
      const artworksWithTags = new Set(tagRows.map(t => t.vc_id)).size;

      // Batch: prints (chunked)
      const printRows = await fetchInChunks('products', 'vc_id', 'vc_id', vcIds, q => q.eq('type', 'print'));
      const artworksWithPrints = new Set(printRows.map(p => p.vc_id)).size;

      // Batch: media_elements — images + audio (story comes from vcs.more now)
      const mediaRows = await fetchInChunks('media_elements', 'vc_id, type', 'vc_id', vcIds, q => q.in('type', ['Image', 'Audio']));
      const mediaByVc = {};
      mediaRows.forEach(m => {
        if (!mediaByVc[m.vc_id]) mediaByVc[m.vc_id] = new Set();
        mediaByVc[m.vc_id].add(m.type);
      });
      let artworksWithImages = 0, artworksWithAudio = 0;
      Object.values(mediaByVc).forEach(types => {
        if (types.has('Image')) artworksWithImages++;
        if (types.has('Audio')) artworksWithAudio++;
      });

      // Story count: from vcs.more JSON (process / inspiration)
      let artworksWithStory = 0;

      // Direct counts from vcs columns
      let withTitle = 0, withStatus = 0, withPrice = 0, withLocation = 0, withCompletionDate = 0, withDimensions = 0;
      allArtworks.forEach(a => {
        if (a.title && a.title.trim()) withTitle++;
        if (a.status && a.status.trim()) withStatus++;
        if (a.price && a.price > 0) withPrice++;
        if (a.location) withLocation++;
        if (a.artwork_completed_date) withCompletionDate++;
        if (a.width && a.height) withDimensions++;
        // Story: check vcs.more JSON for process or inspiration
        const more = a.more;
        if (more && ((more.process && more.process.trim()) || (more.inspiration && more.inspiration.trim()))) {
          artworksWithStory++;
        }
      });

      // Recent uploads (7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const recentUploads = allArtworks.filter(a => new Date(a.created_at) >= sevenDaysAgo).length;

      // In-collection count from the vcIds we already have
      const colMappingRows = await fetchInChunks('collection_vc_mapping', 'vc_id', 'vc_id', vcIds, q => q.eq('accepted', true));
      const inCollections = new Set(colMappingRows.map(r => r.vc_id)).size;
      const uniqueColIds = new Set(colMappingRows.map(r => r.collection_id)).size;

      setEnrichmentStats({
        total,
        recentUploads,
        inCollections,
        uniqueCollections: uniqueColIds,
        title: withTitle,
        status: withStatus,
        price: withPrice,
        location: withLocation,
        prints: artworksWithPrints,
        completionDate: withCompletionDate,
        dimensions: withDimensions,
        tags: artworksWithTags,
        story: artworksWithStory,
        additionalImages: artworksWithImages,
        audio: artworksWithAudio,
      });
    } catch (error) {
      console.error('Error fetching enrichment stats:', error);
    } finally {
      setEnrichmentLoading(false);
    }
  }, [artistFilter]);

  // Fetch artworks when filters, search, or pagination changes
  useEffect(() => {
    fetchArtworks();
  }, [searchQuery, priceFilter, audioFilter, sortBy, sortOrder, currentPage, pageSize, artistFilter, collectionFilterId]);

  // Fetch enrichment stats on component mount
  useEffect(() => {
    fetchEnrichmentStats();
  }, []);

  // Reload enrichment scorecard when search or collection filter changes
  useEffect(() => {
    fetchEnrichmentStats({ collectionId: collectionFilterId, search: searchQuery });
  }, [collectionFilterId, searchQuery, fetchEnrichmentStats]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1); // Reset to first page when filters change
    if (filterType === 'price') {
      setPriceFilter(prev => ({ ...prev, [value.target.name]: value.target.value }));
    }
  };

  // Handle sort changes
  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // Handle view mode toggle
  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);

  // Format price
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  // Format dimensions
  const formatDimensions = (width, height, depth) => {
    if (!width && !height) return 'N/A';
    let dims = `${width || 0}" × ${height || 0}"`;
    if (depth) dims += ` × ${depth}"`;
    return dims;
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Lightbox functions
  const openLightbox = (artwork) => {
    setSelectedArtwork(artwork);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedArtwork(null);
  };

  // Collection selector functions
  const openCollectionSelector = async (artwork) => {
    setSelectedArtwork(artwork);
    setCollectionSelectorOpen(true);
    
    // Fetch current collections for this artwork when opening selector
    try {
      const { data: collections, error } = await supabase
        .from('collection_vc_mapping')
        .select(`
          collection_id,
          collections!inner(title, description)
        `)
        .eq('vc_id', artwork.vc_id);

      if (error) {
        console.error('Error fetching collections for artwork:', error);
        setCurrentCollections([]);
        return;
      }
      
      // Transform the data to get collection details and sort by title
      const transformedCollections = collections
        .map(item => ({
          collection_id: item.collection_id,
          title: item.collections.title,
          description: item.collections.description
        }))
        .sort((a, b) => a.title.localeCompare(b.title)); // Sort alphabetically by title
      
      setCurrentCollections(transformedCollections);
    } catch (error) {
      console.error('Error in openCollectionSelector:', error);
      setCurrentCollections([]);
    }
  };

  const closeCollectionSelector = () => {
    setCollectionSelectorOpen(false);
    setSelectedArtwork(null);
  };

  const addToCollection = async (collectionId) => {
    if (!selectedArtwork) return;

    try {
      // Check if artwork is already in this collection to prevent duplicates
      const { data: existingMapping, error: checkError } = await supabase
        .from('collection_vc_mapping')
        .select('collection_id, vc_id')
        .eq('collection_id', collectionId)
        .eq('vc_id', selectedArtwork.vc_id)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing mapping:', checkError);
        alert('Failed to check if artwork is already in collection.');
        return;
      }

      if (existingMapping) {
        alert('This artwork is already in the selected collection!');
        return;
      }

      // Insert the new mapping
      const { error } = await supabase
        .from('collection_vc_mapping')
        .insert({
          collection_id: collectionId,
          vc_id: selectedArtwork.vc_id,
        });

      if (error) {
        console.error('Error adding artwork to collection:', error);
        alert('Failed to add artwork to collection.');
        return;
      }

      // Save the last selected collection for quick access
      const selectedCollection = availableCollections.find(c => c.collection_id === collectionId);
      if (selectedCollection) {
        setLastSelectedCollection(selectedCollection);
      }

      // Update artwork's collection count
      const { count: newCollectionCount } = await supabase
        .from('collection_vc_mapping')
        .select('*', { count: 'exact', head: true })
        .eq('vc_id', selectedArtwork.vc_id);

      setArtworks(prevArtworks =>
        prevArtworks.map(artwork =>
          artwork.vc_id === selectedArtwork.vc_id ? { ...artwork, collection_count: newCollectionCount || 0 } : artwork
        )
      );

      alert('Artwork added to collection successfully!');
      closeCollectionSelector();
    } catch (error) {
      console.error('Error in addToCollection:', error);
      alert('Failed to add artwork to collection.');
    }
  };

  // Fetch available collections for selector
  const fetchAvailableCollections = useCallback(async () => {
    try {
      const { data: collections, error } = await supabase
        .from('collections')
        .select('collection_id, title')
        .in('type', ['collection', 'show', 'event']) // Only show collections with these types
        .order('title');

      if (error) {
        console.error('Error fetching collections for selector:', error);
        setAvailableCollections([]);
        return;
      }
      setAvailableCollections(collections);
    } catch (error) {
      console.error('Error in fetchAvailableCollections:', error);
      setAvailableCollections([]);
    }
  }, []);

  useEffect(() => {
    fetchAvailableCollections();
  }, [fetchAvailableCollections]);

  // Fetch collections for the artwork when lightbox is opened
  useEffect(() => {
    const fetchCollectionsForArtwork = async () => {
      if (selectedArtwork) {
        try {
          const { data: collections, error } = await supabase
            .from('collection_vc_mapping')
            .select(`
              collection_id,
              collections!inner(title, description)
            `)
            .eq('vc_id', selectedArtwork.vc_id);

          if (error) {
            console.error('Error fetching collections for artwork:', error);
            setCurrentCollections([]);
            return;
          }
          
          // Transform the data to get collection details and sort by title
          const transformedCollections = collections
            .map(item => ({
              collection_id: item.collection_id,
              title: item.collections.title,
              description: item.collections.description
            }))
            .sort((a, b) => a.title.localeCompare(b.title)); // Sort alphabetically by title
          
          setCurrentCollections(transformedCollections);
        } catch (error) {
          console.error('Error in fetchCollectionsForArtwork:', error);
          setCurrentCollections([]);
        }
      }
    };

    fetchCollectionsForArtwork();
  }, [selectedArtwork]);

  // Load collections for the filter dropdown
  useEffect(() => {
    const loadFilterCollections = async () => {
      try {
        // Get collections with artwork counts, excluding portfolio/profile_order types
        const { data: mappings } = await supabase
          .from('collection_vc_mapping')
          .select('collection_id, collections!inner(collection_id, title, type)')
          .not('collections.type', 'in', '("portfolio","profile_order")');

        if (!mappings) return;

        const countMap = {};
        const infoMap = {};
        mappings.forEach(m => {
          const cid = m.collection_id;
          countMap[cid] = (countMap[cid] || 0) + 1;
          if (!infoMap[cid]) {
            infoMap[cid] = { collection_id: cid, title: m.collections.title };
          }
        });

        const collList = Object.values(infoMap)
          .map(c => ({ ...c, artwork_count: countMap[c.collection_id] || 0 }))
          .sort((a, b) => (a.title || '').localeCompare(b.title || ''));

        setFilterCollections(collList);
      } catch (err) {
        console.error('Error loading filter collections:', err);
      }
    };
    loadFilterCollections();
  }, []);

  // Click-outside handler for collection filter dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (collectionFilterRef.current && !collectionFilterRef.current.contains(e.target)) {
        setShowCollectionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  return (
    <div className="artwork-admin">
        {/* Header */}
        <div className="artwork-header">
          <h1 className="artwork-title">Artwork Management</h1>
        </div>

      {/* Enrichment Scorecard */}
      <div className={`enrichment-scorecard ${enrichmentLoading ? 'is-loading' : ''}`}>
        <div className="enrichment-header">
          <div className="enrichment-header-left">
            <BarChart3 size={16} color="#6366f1" />
            <span className="enrichment-label">Enrichment</span>
            {enrichmentStats && enrichmentStats.total > 0 && (
              <div className="enrichment-quick-stats">
                <span className="quick-stat" title="Total artworks">
                  <Image size={12} /> {enrichmentStats.total}
                </span>
                <span className="quick-stat" title="Recent uploads (7 days)">
                  <Calendar size={12} /> {enrichmentStats.recentUploads ?? 0} new
                </span>
                <span className="quick-stat" title={`In ${enrichmentStats.uniqueCollections ?? 0} collections`}>
                  <BookOpen size={12} /> {enrichmentStats.inCollections ?? 0} in collections
                </span>
                <span className="quick-stat" title="Artworks with audio">
                  <Volume2 size={12} /> {enrichmentStats.audio ?? 0} audio
                </span>
              </div>
            )}
          </div>
          <div className="enrichment-header-right">
            {enrichmentLoading && <span className="enrichment-analyzing">Analyzing...</span>}
            {enrichmentStats && !enrichmentLoading && (
              <span className="enrichment-total">{enrichmentStats.total} analyzed</span>
            )}
            <button
              className="enrichment-refresh"
              onClick={() => fetchEnrichmentStats({ collectionId: collectionFilterId, search: searchQuery })}
              disabled={enrichmentLoading}
              title="Refresh scorecard"
            >
              <RefreshCw size={13} className={enrichmentLoading ? 'spinning' : ''} />
            </button>
          </div>
        </div>
        {enrichmentStats && enrichmentStats.total > 0 && (
          <div className="enrichment-bars">
            {[
              { key: 'title', label: 'Title', icon: FileText, color: '#10b981' },
              { key: 'status', label: 'Status', icon: CheckCircle2, color: '#10b981' },
              { key: 'price', label: 'Price', icon: DollarSign, color: '#10b981' },
              { key: 'dimensions', label: 'Dimensions', icon: Image, color: '#6366f1' },
              { key: 'completionDate', label: 'Completion Date', icon: Calendar, color: '#6366f1' },
              { key: 'location', label: 'Location', icon: MapPin, color: '#8b5cf6' },
              { key: 'tags', label: 'Tags', icon: Tag, color: '#8b5cf6' },
              { key: 'prints', label: 'Prints', icon: Printer, color: '#f59e0b' },
              { key: 'story', label: 'Story', icon: FileText, color: '#f59e0b' },
              { key: 'additionalImages', label: 'Add\'l Images', icon: ImagePlus, color: '#ef4444' },
              { key: 'audio', label: 'Audio Story', icon: Mic, color: '#ef4444' },
            ].map(({ key, label, icon: Icon, color }) => {
              const count = enrichmentStats[key] || 0;
              const pct = Math.round((count / enrichmentStats.total) * 100);
              return (
                <div
                  key={key}
                  className="enrichment-bar-item clickable"
                  onClick={() => navigate(`/admin/enrichment?category=${key}${collectionFilterId ? `&collection=${collectionFilterId}` : ''}`)}
                  title={`Explore ${label} — click for details`}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="enrichment-bar-label">
                    <Icon size={14} color={color} />
                    <span>{label}</span>
                    <span className="enrichment-bar-pct">{pct}%</span>
                  </div>
                  <div className="enrichment-bar-track">
                    <div
                      className="enrichment-bar-fill"
                      style={{ width: `${pct}%`, backgroundColor: color }}
                    />
                  </div>
                  <span className="enrichment-bar-count">{count}/{enrichmentStats.total}</span>
                </div>
              );
            })}
          </div>
        )}
        {!enrichmentStats && enrichmentLoading && (
          <div className="enrichment-loading-placeholder">Loading scorecard data...</div>
        )}
      </div>

      {/* Controls — single compact row */}
      <div className="aa-controls">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <Search size={14} className="search-icon" color="#94a3b8" />
            <input
              type="text"
              placeholder="Search artworks, artists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery('')} className="clear-search-btn" title="Clear search">
                <X size={14} color="#94a3b8" />
              </button>
            )}
          </div>
        </form>

        {filterCollections.length > 0 && (
          <div className="aa-collection-filter" ref={collectionFilterRef}>
            <BookOpen size={13} />
            <input
              type="text"
              placeholder="Collection..."
              value={collectionFilterSearch}
              onChange={(e) => {
                setCollectionFilterSearch(e.target.value);
                setShowCollectionDropdown(true);
                if (!e.target.value) {
                  setCollectionFilterId('');
                  setCurrentPage(1);
                }
              }}
              onFocus={() => setShowCollectionDropdown(true)}
            />
            {collectionFilterSearch && (
              <button className="aa-collection-clear" onClick={() => {
                setCollectionFilterSearch('');
                setCollectionFilterId('');
                setShowCollectionDropdown(false);
                setCurrentPage(1);
              }}><X size={13} /></button>
            )}
            {showCollectionDropdown && (
              <div className="aa-collection-dropdown">
                {filterCollections
                  .filter(c => (c.title || '').toLowerCase().includes(collectionFilterSearch.toLowerCase()))
                  .slice(0, 20)
                  .map(c => (
                    <button
                      key={c.collection_id}
                      className={`aa-collection-option ${collectionFilterId === String(c.collection_id) ? 'active' : ''}`}
                      onClick={() => {
                        setCollectionFilterId(String(c.collection_id));
                        setCollectionFilterSearch(c.title);
                        setShowCollectionDropdown(false);
                        setCurrentPage(1);
                      }}
                    >
                      <span>{c.title}</span>
                      <span className="aa-collection-count">{c.artwork_count}</span>
                    </button>
                  ))
                }
                {filterCollections.filter(c => (c.title || '').toLowerCase().includes(collectionFilterSearch.toLowerCase())).length === 0 && (
                  <div className="aa-collection-empty">No collections found</div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="controls-separator" />

        <div className="view-controls">
          <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>
            <Grid3X3 size={14} />
          </button>
          <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>
            <List size={14} />
          </button>
        </div>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="sort-select"
        >
          <option value="created_at">Date</option>
          <option value="title">Title</option>
          <option value="price">Price</option>
          <option value="artist">Artist</option>
        </select>
        
        <button
          className={`sort-direction-btn ${sortOrder === 'asc' ? 'asc' : 'desc'}`}
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
        >
          {sortOrder === 'asc' ? '↑' : '↓'}
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading artworks...</p>
          {loadingProgress.total > 0 && (
            <div className="loading-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(loadingProgress.current / loadingProgress.total) * 100}%` }}
                ></div>
              </div>
              <p className="progress-text">
                Processing {loadingProgress.current} of {loadingProgress.total} artworks...
              </p>
            </div>
          )}
        </div>
      )}

      {/* Page Loading State */}
      {isPageLoading && !loading && (
        <div className="page-loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading page {currentPage}...</p>
        </div>
      )}

      {/* Artworks Container */}
      {!loading && (
        <div className={`artworks-container ${viewMode}`}>
          {viewMode === 'grid' ? (
            // Grid View - Updated to match CollectionDetailAdmin style
            <div className="artworks-grid">
              {artworks.map((artwork) => (
                <div key={artwork.vc_id} className="artwork-card-new">
                  {/* Enrichment Score Badge */}
                  <div className={`enrichment-badge ${artwork.enrichment_score >= 9 ? 'high' : artwork.enrichment_score >= 6 ? 'medium' : 'low'}`}
                    title={`Enrichment: ${artwork.enrichment_score}/11 — Missing: ${Object.entries(artwork.enrichment || {}).filter(([,v]) => !v).map(([k]) => k.replace('has', '')).join(', ')}`}
                  >
                    {artwork.enrichment_score}/11
                  </div>

                  {/* Image Container - Larger, cleaner */}
                  <div className="artwork-image-container" onClick={() => openLightbox(artwork)}>
                    <img 
                      src={getDisplayImageUrl(artwork.cover_image) || '/images/placeholder-artwork.jpg'} 
                      alt={artwork.title}
                      className="artwork-image"
                      onError={(e) => {
                        e.target.src = '/images/placeholder-artwork.jpg';
                      }}
                    />
                    
                    {/* Minimal overlay badges */}
                    <div className="artwork-overlay">
                      <div className="overlay-icon">
                        <Eye size={24} color="#ffffff" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Compact Info Section */}
                  <div className="artwork-card-info">
                    <div className="artwork-header">
                      <h4 className="artwork-title">{artwork.title}</h4>
                      <p className="artwork-artist">{artwork.artist_name}</p>
                    </div>
                    
                    {/* Status pill */}
                    {artwork.status && (
                      <span className={`status-pill ${artwork.status === 'For Sale' ? 'for-sale' : artwork.status === 'Sold' ? 'sold' : 'not-for-sale'}`}>
                        {artwork.status}
                      </span>
                    )}

                    {/* Quick Stats Row */}
                    <div className="artwork-stats-row">
                      {artwork.width && artwork.height && (
                        <span className="dimensions">{artwork.width}"×{artwork.height}"</span>
                      )}
                      {artwork.price > 0 && (
                        <span className="price">${artwork.price.toLocaleString()}</span>
                      )}
                      <span className="collections-count">{artwork.collection_count} collections</span>
                    </div>

                    {/* Enrichment Dots */}
                    <div className="enrichment-dots" title="Enrichment fields present">
                      {artwork.enrichment?.hasTags && <span className="dot dot-tags" title="Tags"><Tag size={11}/></span>}
                      {artwork.enrichment?.hasStory && <span className="dot dot-story" title="Story"><FileText size={11}/></span>}
                      {artwork.enrichment?.hasAudio && <span className="dot dot-audio" title="Audio"><Mic size={11}/></span>}
                      {artwork.enrichment?.hasAdditionalImages && <span className="dot dot-images" title="Add'l Images"><ImagePlus size={11}/></span>}
                      {artwork.enrichment?.hasPrints && <span className="dot dot-prints" title="Prints"><Printer size={11}/></span>}
                      {artwork.enrichment?.hasLocation && <span className="dot dot-location" title="Location"><MapPin size={11}/></span>}
                      {artwork.enrichment?.hasCompletionDate && <span className="dot dot-date" title="Completion Date"><Calendar size={11}/></span>}
                    </div>
                  </div>
                  
                  {/* Action Buttons Row - Clean, horizontal layout */}
                  <div className="artwork-actions-row">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openCollectionSelector(artwork);
                      }}
                      className="action-btn collection-btn"
                      title="Add to collection"
                    >
                      <BookOpen size={14} />
                      Add to Collection
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/artworks/${artwork.vc_id}/edit`);
                      }}
                      className="action-btn edit-btn"
                      title="Edit artwork details"
                    >
                      <Edit3 size={14} />
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // List View
            <div className="artwork-table">
              <div className="table-header">
                <div className="header-cell">Image</div>
                <div className="header-cell">Title</div>
                <div className="header-cell">Artist</div>
                <div className="header-cell">Status</div>
                <div className="header-cell">Price</div>
                <div className="header-cell">Score</div>
                <div className="header-cell">Collections</div>
                <div className="header-cell">Actions</div>
              </div>
              
              {artworks.map((artwork) => (
                <div key={artwork.vc_id} className="artwork-row">
                  <div className="cell-image" onClick={() => openLightbox(artwork)}>
                    <img 
                      src={getDisplayImageUrl(artwork.cover_image) || '/images/placeholder-artwork.jpg'} 
                      alt={artwork.title}
                      onError={(e) => {
                        e.target.src = '/images/placeholder-artwork.jpg';
                      }}
                    />
                  </div>
                  <div className="cell-title">{artwork.title}</div>
                  <div className="cell-artist">{artwork.artist_name}</div>
                  <div className="cell-status">
                    {artwork.status && (
                      <span className={`status-pill ${artwork.status === 'For Sale' ? 'for-sale' : artwork.status === 'Sold' ? 'sold' : 'not-for-sale'}`}>
                        {artwork.status}
                      </span>
                    )}
                  </div>
                  <div className="cell-price">{formatPrice(artwork.price)}</div>
                  <div className="cell-score">
                    <span className={`enrichment-badge-inline ${artwork.enrichment_score >= 9 ? 'high' : artwork.enrichment_score >= 6 ? 'medium' : 'low'}`}>
                      {artwork.enrichment_score}/11
                    </span>
                  </div>
                  <div className="cell-collections">
                    <button 
                      className="collection-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCollectionSelector(artwork);
                      }}
                      title="Click to view/add to collections"
                    >
                      {artwork.collection_count} collections
                    </button>
                  </div>
                  <div className="cell-actions">
                    <button className="action-btn view-btn" title="View" onClick={() => handleArtworkClick(artwork.vc_id)}>
                      <Eye size={14} color="#007bff" />
                    </button>
                    <button className="action-btn edit-btn" title="Edit">
                      <Edit size={14} color="#6c757d" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalCount > 0 && (
        <div className="pagination">
          <div className="pagination-info">
            <p>Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} artworks</p>
          </div>
          
          <div className="pagination-controls">
            <button
              className="page-btn"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </button>
            
            <div className="page-numbers">
              {/* Show first page */}
              {currentPage > 3 && (
                <>
                  <button
                    className="page-btn"
                    onClick={() => handlePageChange(1)}
                  >
                    1
                  </button>
                  {currentPage > 4 && <span className="page-ellipsis">...</span>}
                </>
              )}
              
              {/* Show pages around current page */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, currentPage - 2) + i;
                if (pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    className={`page-btn ${pageNum === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              {/* Show last page */}
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && <span className="page-ellipsis">...</span>}
                  <button
                    className="page-btn"
                    onClick={() => handlePageChange(totalPages)}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>
            
            <button
              className="page-btn"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </div>
          
          <div className="pagination-size">
            <label htmlFor="page-size">Items per page:</label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => {
                const newSize = parseInt(e.target.value);
                setPageSize(newSize);
                setCurrentPage(1); // Reset to first page when changing size
              }}
              className="page-size-select"
            >
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={48}>48</option>
              <option value={96}>96</option>
            </select>
          </div>
        </div>
      )}

      {/* No Results */}
      {!loading && artworks.length === 0 && (
        <div className="no-results">
          <Image size={48} color="#6c757d" />
          <h3>No artworks found</h3>
          <p>Try adjusting your search criteria or filters</p>
        </div>
      )}

      {/* Lightbox - Updated to match CollectionDetailAdmin style */}
      {lightboxOpen && selectedArtwork && (
        <div className="artwork-modal" onClick={closeLightbox}>
          <div className="artwork-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="artwork-modal-header">
              <h3>{selectedArtwork.title}</h3>
              <button onClick={closeLightbox}>×</button>
            </div>
            <div className="artwork-modal-body">
              <div className="artwork-modal-image">
                <img 
                  src={getDisplayImageUrl(selectedArtwork.cover_image) || '/images/placeholder-artwork.jpg'} 
                  alt={selectedArtwork.title}
                />
              </div>
              <div className="artwork-modal-details">
                <p><strong>Artist:</strong> {selectedArtwork.artist_name}</p>
                {selectedArtwork.width && selectedArtwork.height && (
                  <p><strong>Dimensions:</strong> {formatDimensions(selectedArtwork.width, selectedArtwork.height, selectedArtwork.depth)}</p>
                )}
                {selectedArtwork.price && <p><strong>Price:</strong> {formatPrice(selectedArtwork.price)}</p>}
                {selectedArtwork.medium && <p><strong>Medium:</strong> {selectedArtwork.medium}</p>}
                {selectedArtwork.description && <p><strong>Description:</strong> {selectedArtwork.description}</p>}
                <p><strong>Created:</strong> {formatDate(selectedArtwork.created_at)}</p>
                <p><strong>Collections:</strong> {selectedArtwork.collection_count} collections</p>
                
                <div className="artwork-engagement-details">
                  <h4>Actions</h4>
                  <div className="collection-actions">
                    <button 
                      className="view-details-btn"
                      onClick={() => {
                        navigate(`/artwork/${selectedArtwork.vc_id}`);
                        closeLightbox();
                      }}
                      title="View public artwork page"
                    >
                      <Eye size={16} />
                      View Details
                    </button>
                    {lastSelectedCollection && (
                      <button 
                        className="quick-add-btn-lightbox"
                        onClick={() => addToCollection(lastSelectedCollection.collection_id)}
                        title={`Quick add to ${lastSelectedCollection.title}`}
                      >
                        <Plus size={16} />
                        Quick Add to {lastSelectedCollection.title}
                      </button>
                    )}
                    <button 
                      className="add-to-collection-btn"
                      onClick={() => openCollectionSelector(selectedArtwork)}
                    >
                      <Plus size={16} />
                      Add to Collection
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Collection Selector */}
      {collectionSelectorOpen && selectedArtwork && (
        <div className="collection-selector-overlay" onClick={closeCollectionSelector}>
          <div className="collection-selector-content" onClick={(e) => e.stopPropagation()}>
            <div className="collection-selector-header">
              <h3>Add "{selectedArtwork.title}" to Collection</h3>
              <div className="header-actions">
                {lastSelectedCollection && (
                  <button 
                    className="quick-add-btn"
                    onClick={() => addToCollection(lastSelectedCollection.collection_id)}
                    title={`Quick add to ${lastSelectedCollection.title}`}
                  >
                    <Plus size={16} />
                    Quick Add to {lastSelectedCollection.title}
                  </button>
                )}
                <button className="selector-close" onClick={closeCollectionSelector}>
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="collection-selector-body">
              {/* Show existing collections for this artwork */}
              <div className="existing-collections-section">
                <h4>Currently in {selectedArtwork.collection_count} collections:</h4>
                {currentCollections.length > 0 ? (
                  <div className="existing-collections-list">
                    {currentCollections.map((collection) => (
                      <div key={collection.collection_id} className="existing-collection-item">
                        <BookOpen size={16} color="#6c757d" />
                        <span className="collection-name">{collection.title}</span>
                        <button 
                          className="view-collection-link"
                          onClick={() => {
                            navigate(`/admin/collections/${collection.collection_id}`);
                            closeCollectionSelector();
                          }}
                          title="View this collection"
                        >
                          <Eye size={14} />
                          View
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-existing-collections">Not in any collections yet.</p>
                )}
              </div>
              
              <div className="collection-divider">
                <span>Add to another collection:</span>
              </div>
              
              <div className="collection-search">
                <input 
                  type="text" 
                  placeholder="Search collections..."
                  className="collection-search-input"
                />
              </div>
              
              <div className="collections-list">
                {availableCollections.map((collection) => (
                  <button
                    key={collection.collection_id}
                    className="collection-option"
                    onClick={() => addToCollection(collection.collection_id)}
                  >
                    <BookOpen size={16} />
                    <span>{collection.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Collections View Modal */}
      {collectionsViewOpen && selectedArtwork && (
        <div className="collections-view-overlay" onClick={() => setCollectionsViewOpen(false)}>
          <div className="collections-view-content" onClick={(e) => e.stopPropagation()}>
            <div className="collections-view-header">
              <h3>Collections for "{selectedArtwork.title}"</h3>
              <button className="view-close" onClick={() => setCollectionsViewOpen(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="collections-view-body">
              {currentCollections.length > 0 ? (
                <div className="collections-grid">
                  {currentCollections.map((collection) => (
                    <div key={collection.collection_id} className="collection-card">
                      <div className="collection-card-header">
                        <BookOpen size={20} color="#7c3aed" />
                        <h4>{collection.title}</h4>
                      </div>
                      {collection.description && (
                        <p className="collection-description">{collection.description}</p>
                      )}
                      <div className="collection-actions">
                        <button 
                          className="view-collection-btn"
                          onClick={() => {
                            // Navigate to collection detail page
                            navigate(`/admin/collections/${collection.collection_id}`);
                          }}
                        >
                          <Eye size={14} />
                          View Collection
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-collections">
                  <BookOpen size={48} color="#9ca3af" />
                  <p>This artwork is not in any collections yet.</p>
                  <button 
                    className="add-to-collection-btn"
                    onClick={() => {
                      setCollectionsViewOpen(false);
                      openCollectionSelector(selectedArtwork);
                    }}
                  >
                    <Plus size={16} />
                    Add to Collection
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
        </div>
    );
  };

export default ArtworkAdmin;
